import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./myServices.css";
import CreateServiceModal from "../../Service/CreateServiceModal";
import EditServiceModal from "../../Service/EditServiceModal";
import AddListingModal from "./AddListingModal";
import VehicleCard from "../../Pages/YourBussiness/Dealerships/VehicleCard";
import VehicleItemSkeleton from "../../Pages/YourBussiness/Dealerships/VehicleItemSkeleton";
import VehicleModal from "../../Pages/YourBussiness/Dealerships/VehicleModal";
import {
  getVehicles,
  deleteVehicle,
} from "../../api/dealership/dealershpService";
import { PrimaryButton } from "../../components/buttons/primaryButton/PrimaryButton";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { FiTool, FiHome, FiPlus, FiChevronRight } from "react-icons/fi";
import { CiMoneyBill, CiClock2 } from "react-icons/ci";
import { Car, Plus } from "lucide-react";
import toast from "react-hot-toast";
import { getCurrencySymbol } from "../../utils/currency/getCurrencySymbol";
import { smartDuration } from "../../utils/duration/smartDuration.ts";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import { getIndustryThunk } from "../../redux/slices/profileSlice/asyncthunk";
import { selectIndustryData } from "../../redux/slices/profileSlice/ProfileSlice";
const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  return num % 1 === 0
    ? num.toLocaleString()
    : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatDuration = (minutes) => {
  const total = Number(minutes);
  if (!total && total !== 0) return "—";
  if (Number.isNaN(total)) return "—";

  const h = Math.floor(total / 60);
  const m = total % 60;

  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const getListingPriceLabel = (listing, t) => {
  const type = String(listing?.listing_type || "").toLowerCase();

  if (type === "sell" || type === "sale") {
    return `${t("auth.prefixSale")}${formatMoney(listing?.sell_price)}`;
  }

  if (type === "rent") {
    if (listing?.monthly_rent_price) {
      return `${t("auth.prefixRent")}${formatMoney(listing.monthly_rent_price)}${t("auth.suffixMonthly")}`;
    }
    if (listing?.yearly_rent_price) {
      return `${t("auth.prefixRent")}${formatMoney(listing.yearly_rent_price)}${t("auth.suffixYearly")}`;
    }
  }

  return `${t("auth.prefixPrice")}${formatMoney(
    listing?.sell_price ||
      listing?.monthly_rent_price ||
      listing?.yearly_rent_price,
  )}`;
};

function SegmentedTabs({ activeTab, onChange, isRealEstate, isDealerships }) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: 6,
        borderRadius: 18,
        background: "#f3f4f6",
        border: "1px solid #e5e7eb",
        position: "relative",
      }}
    >
      <button
        type="button"
        onClick={() => onChange("services")}
        style={{
          border: "none",
          background: activeTab === "services" ? "#ffffff" : "transparent",
          color: activeTab === "services" ? "#111827" : "#6b7280",
          padding: "10px 16px",
          borderRadius: 14,
          fontWeight: 600,
          cursor: "pointer",
          boxShadow:
            activeTab === "services" ? "0 1px 2px rgba(16,24,40,0.06)" : "none",
          transition: "all 0.2s ease",
        }}
      >
        {t("auth.servicesTab")}
      </button>

      {isRealEstate && (
        <button
          type="button"
          onClick={() => onChange("listings")}
          style={{
            border: "none",
            background: activeTab === "listings" ? "#ffffff" : "transparent",
            color: activeTab === "listings" ? "#111827" : "#6b7280",
            padding: "10px 16px",
            borderRadius: 14,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow:
              activeTab === "listings"
                ? "0 1px 2px rgba(16,24,40,0.06)"
                : "none",
            transition: "all 0.2s ease",
          }}
        >
          {t("auth.listingsTab")}
        </button>
      )}

      {isDealerships && (
        <button
          type="button"
          onClick={() => onChange("dealerships")}
          style={{
            border: "none",
            background: activeTab === "dealerships" ? "#ffffff" : "transparent",
            color: activeTab === "dealerships" ? "#111827" : "#6b7280",
            padding: "10px 16px",
            borderRadius: 14,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow:
              activeTab === "dealerships"
                ? "0 1px 2px rgba(16,24,40,0.06)"
                : "none",
            transition: "all 0.2s ease",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Car size={16} />
          {t("home.myDealerships", { defaultValue: "Dealerships" })}
        </button>
      )}
    </div>
  );
}

function EmptyState({ icon, title, description }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        border: "1px dashed #d1d5db",
        background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
        borderRadius: 24,
        padding: 32,
        minHeight: 280,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div
          style={{
            width: 72,
            height: 72,
            margin: "0 auto 18px",
            borderRadius: 22,
            background: "#f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#4b5563",
          }}
        >
          {icon}
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 22,
            lineHeight: 1.2,
            fontWeight: 700,
            color: "#111827",
          }}
        >
          {title}
        </h3>
        <p
          style={{
            marginTop: 10,
            marginBottom: 0,
            color: "#6b7280",
            fontSize: 15,
            lineHeight: 1.6,
          }}
        >
          {description}
        </p>
      </div>
    </motion.div>
  );
}

function StatPill({ icon, label, value }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 16,
        background: "#f9fafb",
        border: "1px solid #eef2f7",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 12,
          background: "#ffffff",
          border: "1px solid #eef2f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#020637",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "#6b7280" }}>{label}</div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ service, onEdit, currencySymbol }) {
  const { t } = useTranslation();
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        padding: 20,
        boxShadow: "0 6px 20px rgba(16,24,40,0.04)",
        display: "flex",
        flexDirection: "column",
        minHeight: 240,
        height: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          minHeight: 96,
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            borderRadius: 16,
            background: "linear-gradient(135deg, #eef0f8 0%, #f5f3ff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#060e4a",
            flexShrink: 0,
          }}
        >
          <FiTool size={22} />
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                lineHeight: 1.25,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {service.name}
            </h3>

            {service.isFromSuggested && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#060e4a",
                  background: "#eef0f8",
                  padding: "6px 10px",
                  borderRadius: 999,
                }}
              >
                {t("auth.suggestedBadge")}
              </span>
            )}

            {service.isLocal && !service.isFromSuggested && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#92400e",
                  background: "#fffbeb",
                  padding: "6px 10px",
                  borderRadius: 999,
                }}
              >
                {t("auth.localBadge")}
              </span>
            )}
          </div>

          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.6,
              color: "#6b7280",
            }}
          >
            {service.description?.length > 135
              ? `${service.description.slice(0, 135)}...`
              : service.description || t("auth.noDescription")}
          </p>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 10,
          marginTop: "auto",
        }}
      >
        <StatPill
          icon={<CiMoneyBill size={20} />}
          label={t("auth.labelPrice")}
          value={`${currencySymbol}${formatMoney(service.price)}`}
        />
        <StatPill
          icon={<CiClock2 size={18} />}
          label={t("auth.labelDuration")}
          value={smartDuration(
            `${service.duration} ${service.time_unit || "minutes"}`,
          )}
        />
      </div>

      <div style={{ marginTop: "20px" }}>
        <button
          type="button"
          onClick={() => onEdit(service)}
          style={{
            width: "100%",
            border: "none",
            borderRadius: 16,
            padding: "13px 16px",
            background: "#111827",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: 14,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          {t("auth.btnEditService")}
        </button>
      </div>
    </motion.div>
  );
}

function ListingCard({ listing }) {
  const { t } = useTranslation();
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        padding: 20,
        boxShadow: "0 6px 20px rgba(16,24,40,0.04)",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        minHeight: 240,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          minHeight: 96,
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            borderRadius: 16,
            background: "linear-gradient(135deg, #ecfeff 0%, #eff6ff 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#0f766e",
            flexShrink: 0,
          }}
        >
          <FiHome size={22} />
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                lineHeight: 1.25,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {listing.property_name || t("auth.unnamedProperty")}
            </h3>

            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#0f766e",
                background: "#ecfeff",
                padding: "6px 10px",
                borderRadius: 999,
              }}
            >
              {listing.listing_type || "Listing"}
            </span>
          </div>

          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.6,
              color: "#6b7280",
            }}
          >
            {listing.property_description?.length > 135
              ? `${listing.property_description.slice(0, 135)}...`
              : listing.property_description || t("auth.noDescription")}
          </p>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 10,
          marginTop: "auto",
        }}
      >
        <StatPill
          icon={<CiMoneyBill size={20} />}
          label={t("auth.labelPricing")}
          value={getListingPriceLabel(listing, t)}
        />
        <StatPill
          icon={<CiClock2 size={18} />}
          label={t("auth.labelTourDuration")}
          value={formatDuration(listing.tour_duration)}
        />
      </div>
    </motion.div>
  );
}

export default function MyServices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [services, setServices] = useState([]);
  const [listings, setListings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [selectedVehicleForEdit, setSelectedVehicleForEdit] = useState(null);
  const [userType, setUserType] = useState(
    localStorage.getItem("user_status") || "individual",
  );

  const [error, setError] = useState("");
  const [listingError, setListingError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingListings, setLoadingListings] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showListingModal, setShowListingModal] = useState(false);
  const [editService, setEditService] = useState(null);
  const [activeTab, setActiveTab] = useState("services");
  const industry = useAppSelector(selectIndustryData);
  const isFetched = useState(false)[0];
  const isRealEstate = industry?.industry === "Sales & Real Estate";
  const isDealerships = industry?.industry === "Dealerships";
  const [userCurrency, setUserCurrency] = useState(
    localStorage.getItem("user_currency") || "USD",
  );

  const filteredVehicles = useMemo(() => {
    if (vehicleFilter === "all") return vehicles;
    return vehicles.filter(
      (item) => item.type?.toLowerCase() === vehicleFilter.toLowerCase(),
    );
  }, [vehicles, vehicleFilter]);

  const fetchVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    try {
      const res = await getVehicles();
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setVehicles(data);
    } catch (err) {
      console.error("Failed to fetch vehicles:", err);
      toast.error(t("home.failedLoadVehicles") || "Failed to load vehicles");
    } finally {
      setLoadingVehicles(false);
    }
  }, [t]);

  const handleDeleteVehicle = async (id) => {
    try {
      await deleteVehicle(id);
      toast.success(
        t("profile.vehicleDeleted") || "Vehicle deleted successfully",
      );
      fetchVehicles();
    } catch (err) {
      console.error("Failed to delete vehicle:", err);
      toast.error(t("home.failedDeleteVehicle") || "Failed to delete vehicle");
    }
  };

  const handleEditVehicle = (vehicleItem) => {
    setSelectedVehicleForEdit(vehicleItem);
    setShowVehicleModal(true);
  };

  const handleCreateVehicle = () => {
    setSelectedVehicleForEdit(null);
    setShowVehicleModal(true);
  };

  useEffect(() => {
    if (isDealerships) {
      setActiveTab("dealerships");
      fetchVehicles();
    }
  }, [isDealerships, fetchVehicles]);

  useEffect(() => {
    if (activeTab === "dealerships") {
      fetchVehicles();
    }
  }, [activeTab, fetchVehicles]);

  const loadCurrencies = async () => {
    try {
      const token = await getValidToken();
      if (!token) throw new Error(t("auth.errorSessionExpired"));

      const res = await fetch(`${BASE_URL}/service/currencies/`, {
        method: "GET",
        headers: buildHeaders(),
      });

      if (!res.ok) throw new Error(t("auth.errorOnLoadCurrencies"));

      const data = await res.json().catch(() => []);
    } catch (err) {
      console.error("Failed to load currencies:", err);
    }
  };

  useEffect(() => {
    dispatch(getIndustryThunk());
    const timeZone = getUserTimeZone();
    const userStatus = localStorage.getItem("user_status") || "individual";
    const code = userStatus === "salon" ? "228" : "216";

    localStorage.setItem("onboarding_code", code);

    sessionStorage.setItem("onboarding_force_route", "/my-services");
    sessionStorage.setItem("onboarding_force_status", userStatus);
  }, []);

  const getValidToken = async () => {
    let token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken") ||
      "";

    if (!token) return null;

    const verify = await fetch(`${BASE_URL}/auth/token/verify/`, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify({ token }),
    });

    if (verify.ok) return token;

    const refresh =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");

    if (!refresh) return null;

    const res = await fetch(`${BASE_URL}/auth/token/refresh/`, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify({ refresh }),
    });

    const data = await res.json().catch(() => ({}));

    if (res.ok && data.access) {
      const useLocalStorage =
        !!localStorage.getItem("accessToken") ||
        !!localStorage.getItem("refreshToken");

      if (useLocalStorage) {
        localStorage.setItem("accessToken", data.access);
      } else {
        sessionStorage.setItem("accessToken", data.access);
      }

      return data.access;
    }

    return null;
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      setError("");

      const token = await getValidToken();
      if (!token) throw new Error(t("auth.errorSessionExpired"));

      const res = await fetch(`${BASE_URL}/service/my-services/`, {
        method: "GET",
        headers: buildHeaders(),
      });

      if (!res.ok) throw new Error(t("auth.errorOnLoadServices"));

      const data = await res.json().catch(() => []);
      setServices(
        Array.isArray(data)
          ? data.map((service) => ({ ...service, isLocal: false }))
          : [],
      );
    } catch (err) {
      setError(err.message || t("auth.errorOnLoadServices"));
    } finally {
      setLoading(false);
    }
  };

  const loadListings = async () => {
    try {
      setLoadingListings(true);
      setListingError("");

      const token = await getValidToken();
      if (!token) throw new Error(t("auth.errorSessionExpired"));

      const res = await fetch(`${BASE_URL}/service/real-estate-my-listings/`, {
        method: "GET",
        headers: buildHeaders(),
      });

      if (!res.ok) throw new Error(t("auth.errorOnLoadListings"));

      const data = await res.json().catch(() => []);
      const normalized = Array.isArray(data)
        ? data
        : data?.results || data?.data || [];

      setListings(normalized);
    } catch (err) {
      setListingError(err.message || t("auth.errorOnLoadListings"));
      setListings([]);
    } finally {
      setLoadingListings(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("selectedServicesPayload");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const wasClosed = localStorage.getItem("my_services_closed");
    if (wasClosed === "true") {
      localStorage.removeItem("my_services_closed");
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }

    const initPage = async () => {
      let realEstateMode = false;

      await loadCurrencies();
      await loadServices();

      const payload = localStorage.getItem("selectedServicesPayload");

      if (payload && !wasClosed) {
        try {
          const parsed = JSON.parse(payload);

          const converted = parsed.map((srv) => ({
            id: srv.id || Date.now() + Math.random(),
            name: srv.name,
            description: srv.description,
            duration: parseInt(srv.duration, 10),
            price: parseFloat(srv.price),
            service_count: srv.service_count || "1",
            time_unit: srv.time_unit || "minutes",
            on_off: false,
            isLocal: true,
            isFromSuggested: true,
            image_url: srv.image_url || null,
          }));

          setServices((prev) => {
            const existingIds = new Set(prev.map((s) => s.id));
            const newServices = converted.filter((s) => !existingIds.has(s.id));
            return [...prev, ...newServices];
          });
        } catch {}
      }

      if (realEstateMode) {
        await loadListings();
      }
    };

    initPage();

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFetched]);

  const createAllLocalServices = async () => {
    const token = await getValidToken();
    if (!token) throw new Error(t("auth.errorSessionExpired"));

    if (services.length === 0) return [];

    const response = await fetch(`${BASE_URL}/service/service-create/`, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify(
        services.map((service) => ({
          name: service.name,
          description: service.description,
          duration: Number(service.duration),
          time_unit: service.time_unit || "minutes",
          price: parseFloat(service.price),
          currency: "USD",
          service_count: service.service_count,
          on_off: false,
          image_url: service.image_url || null,
          timeZone: getUserTimeZone(),
        })),
      ),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || "Failed to create services");
    }

    localStorage.removeItem("selectedServicesPayload");

    return await response.json().catch(() => []);
  };

  const hasItems =
    services.length > 0 || (isDealerships && vehicles.length > 0);

  const handleNext = async () => {
    if (!hasItems) {
      toast.error(
        isDealerships
          ? t("profile.errorNoVehiclesOnNext") ||
              "Please add at least one vehicle or service to continue"
          : t("auth.errorNoServicesOnNext"),
      );
      return;
    }

    setLoading(true);

    try {
      if (services.length > 0) {
        await createAllLocalServices();
      }

      const userStatus = localStorage.getItem("user_status") || "individual";
      const nextCode = userStatus === "salon" ? "230" : "218";

      localStorage.setItem("onboarding_code", nextCode);
      localStorage.setItem("onboarding_step_completed", "true");

      sessionStorage.setItem("onboarding_force_route", "/working-days");
      sessionStorage.setItem("onboarding_force_status", userStatus);

      navigate("/working-days", { replace: true });
    } catch (error) {
      toast.error(
        t("auth.errorSavingServicesGeneric", { message: error.message }),
      );
    } finally {
      setLoading(false);
    }
  };

  const addNewService = (newService) => {
    const serviceWithId = {
      ...newService,
      id: Date.now(),
      price: parseFloat(newService.price),
      duration: parseInt(newService.duration, 10),
      service_count: newService.service_count,
      isLocal: true,
      isFromSuggested: false,
    };

    setServices((prev) => [...prev, serviceWithId]);
    toast.success(t("auth.toastServiceAddedLocally"));
  };

  const updateService = (updatedService) => {
    setServices((prev) =>
      prev.map((service) =>
        service.id === updatedService.id ? updatedService : service,
      ),
    );
  };

  const removeService = (id) => {
    setServices((prev) => prev.filter((service) => service.id !== id));
    toast.success(t("auth.toastServiceRemoved"));
  };

  const handleListingCreated = async () => {
    toast.success(t("auth.toastListingCreated"));
    setShowListingModal(false);
    await loadListings();
    setActiveTab("listings");
  };

  const showServiceContent =
    activeTab === "services" || (!isRealEstate && !isDealerships);
  const showListingContent = activeTab === "listings" && isRealEstate;
  const showDealershipContent = activeTab === "dealerships" && isDealerships;

  return (
    <motion.div
      className="my-service-page"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1180,
          margin: "0 auto",
          padding: "12px 0 24px",
        }}
      >
        <div
          style={{
            background: "linear-gradient(180deg, #ffffff 0%, #fbfbfd 100%)",
            border: "1px solid #e5e7eb",
            borderRadius: 28,
            boxShadow: "0 16px 50px rgba(16,24,40,0.06)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "26px 26px 20px",
              borderBottom: "1px solid #eef2f7",
              background:
                "radial-gradient(circle at top left, rgba(2, 6, 55,0.08), transparent 32%), #ffffff",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 18,
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 30,
                    lineHeight: 1.1,
                    fontWeight: 500,
                    color: "#111827",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {t("auth.pageTitle")}
                </h1>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <StatPill
                  icon={<FiTool size={16} />}
                  label={t("auth.servicesTab")}
                  value={services.length}
                />
                {isRealEstate && (
                  <StatPill
                    icon={<FiHome size={16} />}
                    label={t("auth.listingsTab")}
                    value={listings.length}
                  />
                )}
                {isDealerships && (
                  <StatPill
                    icon={<Car size={16} />}
                    label={t("home.myDealerships", {
                      defaultValue: "Dealerships",
                    })}
                    value={vehicles.length}
                  />
                )}
              </div>
            </div>

            <div
              style={{
                marginTop: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <SegmentedTabs
                  activeTab={activeTab}
                  onChange={setActiveTab}
                  isRealEstate={isRealEstate}
                  isDealerships={isDealerships}
                />

                {activeTab === "dealerships" && vehicles.length > 0 && (
                  <div className="flex items-center bg-gray-100 rounded-2xl p-1 w-fit max-w-full overflow-x-auto no-scrollbar flex-shrink-0">
                    {[
                      { id: "all", label: t("profile.filterAll") || "All" },
                      { id: "New", label: t("profile.filterNew") || "New" },
                      {
                        id: "Pre-owned",
                        label: t("profile.filterPreOwned") || "Pre-owned",
                      },
                    ].map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setVehicleFilter(item.id)}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                          vehicleFilter === item.id
                            ? "bg-white text-[#020637] shadow-sm"
                            : "text-gray-600 hover:text-gray-800"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                {activeTab === "services" && (
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    disabled={loading}
                    style={{
                      border: "1px solid #d1d5db",
                      background: "#ffffff",
                      color: "#111827",
                      borderRadius: 16,
                      padding: "12px 16px",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <FiPlus size={16} />
                    {t("auth.btnCreateService")}
                  </button>
                )}

                {activeTab === "listings" && isRealEstate && (
                  <button
                    type="button"
                    onClick={() => setShowListingModal(true)}
                    disabled={loadingListings}
                    style={{
                      border: "none",
                      background:
                        "linear-gradient(135deg, #111827 0%, #1f2937 100%)",
                      color: "#ffffff",
                      borderRadius: 16,
                      padding: "12px 16px",
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      boxShadow: "0 10px 25px rgba(17,24,39,0.18)",
                    }}
                  >
                    <FiPlus size={16} />
                    {t("auth.btnAddListing")}
                  </button>
                )}

                {activeTab === "dealerships" &&
                  isDealerships &&
                  userType !== "worker" && (
                    <PrimaryButton
                      onClick={handleCreateVehicle}
                      leftIcon={<Plus className="w-5 h-5" />}
                      label={t("profile.addNewVehicle") || "Add Vehicle"}
                    />
                  )}
              </div>
            </div>
          </div>

          <div
            style={{
              padding: 26,
              maxHeight: "calc(100vh - 290px)",
              minHeight: 340,
              overflowY: "auto",
            }}
          >
            {showServiceContent && (
              <>
                {loading ? (
                  <div
                    style={{
                      minHeight: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6b7280",
                      fontWeight: 600,
                    }}
                  >
                    {t("auth.loadingServices")}
                  </div>
                ) : error ? (
                  <div
                    style={{
                      border: "1px solid #fecaca",
                      background: "#fef2f2",
                      color: "#b91c1c",
                      borderRadius: 18,
                      padding: 16,
                      fontWeight: 600,
                    }}
                  >
                    {error}
                  </div>
                ) : services.length === 0 ? (
                  <EmptyState
                    icon={<FiTool size={30} />}
                    title={t("auth.noServicesTitle")}
                    description={t("auth.noServicesDesc")}
                  />
                ) : (
                  <motion.div
                    layout
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 16,
                    }}
                  >
                    <AnimatePresence>
                      {services.map((service) => (
                        <ServiceCard
                          key={service.id}
                          service={service}
                          onEdit={setEditService}
                          currencySymbol={getCurrencySymbol(userCurrency)}
                        />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                )}
              </>
            )}

            {showListingContent && (
              <>
                {loadingListings ? (
                  <div
                    style={{
                      minHeight: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6b7280",
                      fontWeight: 600,
                    }}
                  >
                    {t("auth.loadingListings")}
                  </div>
                ) : listingError ? (
                  <div
                    style={{
                      border: "1px solid #fecaca",
                      background: "#fef2f2",
                      color: "#b91c1c",
                      borderRadius: 18,
                      padding: 16,
                      fontWeight: 600,
                    }}
                  >
                    {listingError}
                  </div>
                ) : listings.length === 0 ? (
                  <EmptyState
                    icon={<FiHome size={30} />}
                    title={t("auth.noListingsTitle")}
                    description={t("auth.noListingsDesc")}
                  />
                ) : (
                  <motion.div
                    layout
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 16,
                    }}
                  >
                    <AnimatePresence>
                      {listings.map((listing) => (
                        <ListingCard key={listing.id} listing={listing} />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                )}
              </>
            )}

            {showDealershipContent && (
              <>
                {loadingVehicles && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    <VehicleItemSkeleton />
                    <VehicleItemSkeleton />
                    <VehicleItemSkeleton />
                  </div>
                )}

                {!loadingVehicles && vehicles.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <Car className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t("profile.noVehiclesYet") || "No Vehicles Found"}
                    </h3>
                    <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                      {t("profile.getStartedVehicleDesc") ||
                        "Get started by adding your first vehicle to your dealership inventory."}
                    </p>
                  </div>
                )}

                {!loadingVehicles && vehicles.length > 0 && (
                  <>
                    {filteredVehicles.length === 0 ? (
                      <div className="text-center py-16">
                        <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4 border border-gray-100">
                          <Car className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">
                          {t("profile.noVehiclesFiltered") ||
                            "No vehicles match this filter"}
                        </h3>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filteredVehicles.map((vehicleItem, index) => (
                          <VehicleCard
                            key={vehicleItem.id || index}
                            item={vehicleItem}
                            onEdit={handleEditVehicle}
                            onDelete={handleDeleteVehicle}
                            userType={userType}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div
            style={{
              borderTop: "1px solid #eef2f7",
              padding: "18px 26px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              background: "#fcfcfd",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#111827",
                  fontWeight: 700,
                }}
              ></div>
            </div>

            <button
              type="button"
              className={`${!hasItems ? "disabled-btn" : ""}`}
              onClick={handleNext}
              disabled={loading || !hasItems}
              style={{
                border: "none",
                borderRadius: 18,
                padding: "14px 20px",
                background: !hasItems
                  ? "#d1d5db"
                  : "linear-gradient(135deg, #020637 0%, #1e3aa0 100%)",
                color: "#ffffff",
                fontWeight: 800,
                cursor: !hasItems ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                boxShadow: !hasItems
                  ? "none"
                  : "0 14px 30px rgba(2, 6, 55,0.24)",
              }}
            >
              {loading
                ? t("auth.btnSavingToServer")
                : t("auth.btnSaveContinue")}
              {!loading && <FiChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateServiceModal
          onClose={() => setShowCreateModal(false)}
          onServiceCreated={addNewService}
        />
      )}

      {showListingModal && (
        <AddListingModal
          onClose={() => setShowListingModal(false)}
          onCreated={handleListingCreated}
        />
      )}

      {editService && (
        <EditServiceModal
          service={editService}
          onClose={() => setEditService(null)}
          onServiceUpdated={updateService}
          onServiceDeleted={removeService}
        />
      )}

      {showVehicleModal && (
        <VehicleModal
          vehicle={selectedVehicleForEdit}
          onClose={() => {
            setShowVehicleModal(false);
            setSelectedVehicleForEdit(null);
          }}
          onSaved={() => {
            fetchVehicles();
            setShowVehicleModal(false);
            setSelectedVehicleForEdit(null);
          }}
        />
      )}
    </motion.div>
  );
}
